import { generateDeepDiffs } from '@shared/utils/generateDeepDiffs.js';
import filterWorkflowDiffs from '@/lib/buildWorkflowSnapshotDiffs/filterWorkflowDiffs.js';
import filterStepDiffs from '@/lib/buildWorkflowSnapshotDiffs/filterStepDiffs.js';
import hasSameOwnValues from '@/lib/hasSameOwnValues.js';

// The change counter re-runs this on every optimistic and cooked step write while typing, and the
// draft is re-projected on every store change (each step becomes a fresh shallow clone). Per-step
// diffs are therefore cached on the snapshot step and reused while the draft step's own values are
// the same references, so a keystroke re-diffs the edited step instead of the whole workflow.
//
// INVARIANT: a cache hit is only sound because every step write produces new object references down
// the mutated path — `setDeep` rebuilds each level, `PUT_WORKFLOW_STEP` substitutes a whole new step,
// and `applyChangesToStep` clones before writing. Introducing an in-place mutation of a step's
// sub-objects would let a deep change keep its top-level identities and serve a stale diff here.
const stepDiffsBySnapshotStep = new WeakMap();

const getStepDiffs = (oldStep, newStep) => {
  const cached = stepDiffsBySnapshotStep.get(oldStep);
  if (cached && hasSameOwnValues(cached.newStep, newStep)) {
    return cached.diffs;
  }

  const diffs = filterStepDiffs(generateDeepDiffs(oldStep, newStep)).map((n) => ({
    type: oldStep?.type === 'trigger' ? 'trigger' : 'step',
    stepId: oldStep?.id,
    ...n
  }));
  stepDiffsBySnapshotStep.set(oldStep, { newStep, diffs });
  return diffs;
};

const buildWorkflowSnapshotDiffs = (prevObj, newObj) => {
  if (!newObj || !prevObj || !newObj?.steps) {
    return [];
  }

  const newStepDiffs = newObj?.steps
    ?.filter((n) => !prevObj?.steps?.find((o) => o?.id === n?.id))
    .map((n) => ({
      type: 'operation',
      operation: 'add',
      stepId: n?.id,
      newValue: n
    }));

  const oldStepDiffs = prevObj?.steps
    ?.filter((o) => !newObj?.steps?.find((n) => n?.id === o?.id))
    .map((n) => ({
      type: 'operation',
      operation: 'delete',
      appSlug: n?.appSlug,
      message: n?.message,
      stepId: n?.id,
      previousValue: n
    }));

  const stepDiffs = prevObj?.steps
    ?.map((oldStep) => {
      const newStep = newObj?.steps?.find((o) => o?.id === oldStep?.id);
      return !newStep || !oldStep ? null : getStepDiffs(oldStep, newStep);
    })
    .filter((n) => !!n && n?.length > 0)
    .flat();

  const { steps: oldSteps, graph: oldGraph, ...prevWorkflow } = prevObj;
  const { steps: newSteps, graph: newGraph, ...newWorkflow } = newObj;
  const workflowDiffs = filterWorkflowDiffs(generateDeepDiffs(prevWorkflow, newWorkflow)).map((n) => ({
    type: 'workflow',
    workflowId: prevObj?.id,
    ...n
  }));

  return [...workflowDiffs, ...newStepDiffs, ...oldStepDiffs, ...stepDiffs].map((n) => {
    const id = [n.type, n.workflowId, n.stepId, n.key].filter((n) => !!n).join('-');
    return { id, ...n };
  });
};

export default buildWorkflowSnapshotDiffs;
